import type { HiringGridCoord, HiringRecommendation, HiringRecommendationType } from "../types";

/** Separator between a card's identity and a nurse id inside one stored request key. */
const REQUEST_KEY_SEPARATOR = "::";

const slotToken = (slot: HiringGridCoord | undefined | null): string => (slot ? `${slot.weekIdx}-${slot.dayIdx}-${slot.shiftLabel}` : "_");

/**
 * Stable identity for one recommendation card.
 *
 * Built from the coordinates rather than the array index because the requested set is PERSISTED: the
 * backend regenerates recommendations on every request (and re-orders them as severities move), so an
 * index-based key would silently re-point a stored "requested" mark at a different card after a
 * refetch. The coordinates are the thing that doesn't move.
 */
export const recommendationKey = (rec: HiringRecommendation): string => `${rec.type}|${slotToken(rec.fromSlot)}|${slotToken(rec.toSlot)}`;

/** One stored request: which card, and which person on it. */
export const moveRequestKey = (recKey: string, nurseId: number): string => `${recKey}${REQUEST_KEY_SEPARATOR}${nurseId}`;

export const isRequested = (requested: readonly string[], recKey: string, nurseId: number): boolean => requested.includes(moveRequestKey(recKey, nurseId));

/** Any person on this card already asked — drives the card's `Requested` chip. */
export const cardHasRequest = (requested: readonly string[], recKey: string): boolean =>
	requested.some((key) => key.startsWith(`${recKey}${REQUEST_KEY_SEPARATOR}`));

/** Idempotent: asking twice for the same person doesn't grow the stored list. */
export const withRequest = (requested: readonly string[], recKey: string, nurseId: number): string[] => {
	const key = moveRequestKey(recKey, nurseId);
	return requested.includes(key) ? [...requested] : [...requested, key];
};

/**
 * The recommendations of one type, worst first.
 *
 * `hire`-type recommendations are deliberately never surfaced as cards: the Suggested Hires panel
 * answers the same gap as concrete people, and showing both would put one gap on screen twice in two
 * different shapes. Filtering by type at the call site is what keeps that true per tab.
 *
 * Ordering is severity desc, then amount desc — the shipped carousel's order, kept so the card the user
 * saw first still comes first.
 */
/**
 * One request removed. Idempotent, like `withRequest`: clicking `Requested` on a row that is somehow
 * already clear must not throw the list into a different shape.
 */
export const withoutRequest = (requested: readonly string[], recKey: string, nurseId: number): string[] => {
	const key = moveRequestKey(recKey, nurseId);
	return requested.filter((existing) => existing !== key);
};

export const recommendationsOfType = (recommendations: readonly HiringRecommendation[], type: HiringRecommendationType): HiringRecommendation[] =>
	recommendations
		.filter((rec) => rec.type === type)
		.sort((a, b) => {
			const bySeverity = (b.severity ?? 0) - (a.severity ?? 0);
			return bySeverity !== 0 ? bySeverity : b.amount - a.amount;
		});
